import { useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import Modal from "../../shared/Modal.tsx";
import { Activity } from "../../../@types/activities.ts";
import { ActivityPdf, commentCount, GenerateResult } from "./useActivityPdf.ts";

type ActivityPdfModalArgs = {
  activity: Activity;
  pdf: ActivityPdf;
  // The PDF was downloaded or shared: the modal can go away
  onDone: () => void;
  // Fires once the modal is hidden (user dismissed it). The shared Modal also
  // fires it after being unmounted, so the handler must tolerate that.
  onClose: () => void;
};

// Last choice of "include the comments in the PDF", remembered per browser
const INCLUDE_COMMENTS_KEY = "tixxt.activityPdf.includeComments";

function readIncludeComments() {
  try {
    return localStorage.getItem(INCLUDE_COMMENTS_KEY) === "true";
  } catch {
    return false;
  }
}

function storeIncludeComments(value: boolean) {
  try {
    localStorage.setItem(INCLUDE_COMMENTS_KEY, String(value));
  } catch {
    // private mode or blocked storage: the choice just is not remembered
  }
}

// Opened from the "Als PDF herunterladen" share entry when there is something
// to choose (the activity has comments), and when a PDF generated without the
// modal waits for a fresh tap to open the share sheet (mobile).
export default function ActivityPdfModal({
  activity,
  pdf,
  onDone,
  onClose,
}: ActivityPdfModalArgs) {
  const [includeComments, setIncludeComments] = useState(readIncludeComments);

  const generating = pdf.state === "generating";
  const readyToShare = pdf.state === "readyToShare";
  const commentsTotal = commentCount(activity);
  const isQuestion = activity.object.type === "question";

  function finish(result: GenerateResult) {
    if (result === "delivered") onDone();
  }

  function onIncludeCommentsChange(checked: boolean) {
    setIncludeComments(checked);
    storeIncludeComments(checked);
    // A file that waits for a fresh tap (mobile) was built with the old
    // setting: drop it, the next tap generates a new one
    if (readyToShare) pdf.reset();
  }

  async function onDownloadClick() {
    finish(
      await (readyToShare
        ? pdf.shareReadyFile()
        : pdf.generate(commentsTotal > 0 && includeComments)),
    );
  }

  return (
    <Modal
      title={I18n.t("js.activitystreams.share_dialog.pdf")}
      onClose={onClose}
      useMinHeightMax
    >
      <div className="flex flex-col gap-3" data-testid="activity-pdf-modal">
        <p className="mb-0 text-muted">
          {I18n.t("js.activitystreams.share_dialog.pdf_step_hint")}
        </p>
        {commentsTotal > 0 && (
          <label
            className={classNames("checkbox mb-0", {
              "cursor-pointer": !generating,
              "text-muted": generating,
            })}
          >
            <input
              type="checkbox"
              data-testid="share-pdf-include-comments"
              checked={includeComments}
              disabled={generating}
              onChange={(event) =>
                onIncludeCommentsChange(event.target.checked)
              }
            />
            {I18n.t(
              isQuestion
                ? "js.activitystreams.share_dialog.pdf_include_answers_and_comments"
                : "js.activitystreams.share_dialog.pdf_include_comments",
              { count: commentsTotal },
            )}
          </label>
        )}
        {pdf.state === "error" && (
          <p className="mb-0 text-danger" role="alert">
            {I18n.t("js.activitystreams.share_dialog.pdf_error")}
          </p>
        )}
        {pdf.commentsFailed && !generating && (
          <p className="mb-0 text-danger" role="alert">
            {I18n.t("js.activitystreams.share_dialog.pdf_comments_error")}
          </p>
        )}
      </div>
      <div
        className={classNames(
          "form-actions modal-footer mt-6 flex flex-wrap gap-2 justify-end",
          // A phone has no room for a row of buttons: stack them full width
          "max-sm:flex-col-reverse max-sm:[&>*]:w-full",
        )}
      >
        <button
          type="button"
          className="btn btn-link"
          data-testid="share-pdf-cancel"
          data-bs-dismiss="modal"
        >
          {I18n.t("js.cancel")}
        </button>
        {pdf.commentsFailed && (
          <button
            type="button"
            className="btn btn-light"
            data-testid="share-pdf-without-comments"
            onClick={async () => finish(await pdf.generate(false))}
            disabled={generating}
          >
            {I18n.t("js.activitystreams.share_dialog.pdf_without_comments")}
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          data-testid="share-pdf-download"
          onClick={onDownloadClick}
          disabled={generating}
          aria-busy={generating || undefined}
        >
          {generating && (
            <FontAwesomeIcon className="mr-2" icon={faSpinner} spin />
          )}
          {I18n.t(
            generating
              ? "js.activitystreams.share_dialog.pdf_generating"
              : readyToShare
                ? "js.activitystreams.share_dialog.pdf_share"
                : "js.activitystreams.share_dialog.pdf_download",
          )}
        </button>
      </div>
    </Modal>
  );
}
